import React, { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ticketsApi } from '@/api/tickets.api';
import { Modal } from '@/components/ui/Modal';
import { Field } from '@/components/ui/Field';
import { FileUploadField, StagedFile } from '@/components/ui/FileUploadField';
import { MentionTextarea } from '@/components/ui/MentionTextarea';
import { useTagTeammates } from './useTagTeammates';
import { ApiError } from '@/lib/errors';
import { useAuth } from '@/context/AuthContext';
import { useToasts } from '@/context/ToastContext';
import { ViewType } from '@/types/domain';
import { invalidateAfterTicketAction } from '@/lib/queryInvalidation';

interface Props {
  ticketId: string;
  open: boolean;
  onClose: () => void;
}

export function AddNoteModal({ ticketId, open, onClose }: Props) {
  const [content, setContent] = useState('');
  const [isPrivate, setIsPrivate] = useState(false);
  const [attachments, setAttachments] = useState<StagedFile[]>([]);
  const [error, setError] = useState<string | null>(null);
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { push } = useToasts();
  const tag = useTagTeammates();

  const mutation = useMutation({
    mutationFn: async () => {
      const res = await ticketsApi.addNote(ticketId, {
        content,
        is_private: isPrivate,
        attachments: attachments.length > 0 ? attachments : undefined
      } as any);
      // V4.9.13 — after the note saves, fire any explicit teammate tags.
      if (tag.hasContent) await ticketsApi.tag(ticketId, tag.payload());
      return res;
    },
    onSuccess: (data) => {
      // V4.9.3 — backend may auto-submit the ticket for approval when
      // the assigned solver replies. Surface that to the user so they
      // know what just happened.
      if (data?.auto_submitted_for_approval) {
        push({
          kind: 'success',
          title: 'Note added — submitted for approval',
          message: 'Your reply auto-moved this ticket to Waiting Approval and notified the Approver.'
        });
      } else if (data?.auto_handed_off_to_communicator) {
        // V4.9.5+ — non-approval categories (A5/A6/B*): solver's reply
        // hands the ticket off directly to the communicator.
        push({
          kind: 'success',
          title: 'Note added — handed off to communicator',
          message: 'No admin approval needed for this category. Communicator has been notified to inform the customer.'
        });
      } else {
        push({ kind: 'success', title: 'Note added' });
      }
      // Invalidate ALL related caches so the change reflects instantly
      // on the detail page header, list views, and dashboard.
      invalidateAfterTicketAction(queryClient, ticketId);
      setContent('');
      setAttachments([]);
      tag.reset();
      onClose();
    },
    onError: (err: unknown) => {
      setError(err instanceof ApiError ? err.problem?.detail ?? err.message : 'Failed to add note');
    }
  });

  const canPrivate = user?.view_type === ViewType.Admin;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Add note"
      footer={
        <>
          <button type="button" className="btn btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={mutation.isPending || !content.trim()}
            onClick={() => mutation.mutate()}
          >
            {mutation.isPending ? 'Adding...' : 'Add note'}
          </button>
        </>
      }
    >
      <Field
        label="Content"
        required
        hint="Type @ to tag a teammate — they'll get an email and a notification."
      >
        <MentionTextarea
          value={content}
          onChange={setContent}
          rows={4}
          placeholder="Add a note. Type @ to tag someone."
        />
      </Field>
      {canPrivate ? (
        <label className="flex items-center gap-2">
          <input type="checkbox" checked={isPrivate} onChange={(e) => setIsPrivate(e.target.checked)} />
          <span className="text-small">Private note (Admin only)</span>
        </label>
      ) : null}
      <div style={{ marginTop: 12 }}>
        <FileUploadField value={attachments} onChange={setAttachments} />
      </div>
      {tag.render()}
      {error ? <div className="form-error mt-2">{error}</div> : null}
    </Modal>
  );
}
